/**
 * OrdenEditFormDialog - Formulario de edición de órdenes existentes
 * Pre-carga valores actuales y envía PATCH con solo los campos modificados
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Search, X, TestTubes, Package, Calculator, Percent, Loader2 } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import { useApiError } from "@/hooks/use-api-error";
import {
  useOrden,
  useUpdateOrden,
  useBuscarExamenesTipo,
  useBuscarPerfiles,
  type ExamenTipoListItem,
  type PerfilExamenListItem,
  type PrioridadOrden,
  type TipoEntrega,
  type TipoServicio,
} from "@/hooks/use-ordenes";
import { useBuscarMedicosRemitentes } from "@/hooks/use-medicos-remitentes";
import { useBuscarClientes } from "@/hooks/use-clientes";
import { useListasPrecios } from "@/hooks/use-facturacion";
import { useDebounce } from "@/hooks/use-debounce";
import { calcularPrecioAjustado, formatListaPrecioLabel } from "../utils/precio-utils";
import { getMotivoNoEditable } from "../utils/orden-edit-utils";

interface OrdenEditFormDialogProps {
  ordenId: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function OrdenEditFormDialog({ ordenId, open, onOpenChange }: OrdenEditFormDialogProps) {
  const { t } = useTranslation();
  const { getErrorMessage } = useApiError();

  // Form state — editable fields
  const [medicoId, setMedicoId] = useState<number | null>(null);
  const [medicoNombre, setMedicoNombre] = useState("");
  const [clienteId, setClienteId] = useState<number | null>(null);
  const [clienteNombre, setClienteNombre] = useState("");
  const [listaPrecioId, setListaPrecioId] = useState<number | null>(null);
  const [prioridad, setPrioridad] = useState<PrioridadOrden>("NORMAL");
  const [tipoEntrega, setTipoEntrega] = useState<TipoEntrega>("PRESENCIAL");
  const [tipoServicio, setTipoServicio] = useState<TipoServicio>("AMBULATORIO");
  const [observaciones, setObservaciones] = useState("");
  const [descuentoPorcentaje, setDescuentoPorcentaje] = useState("");
  const [descuentoLempiras, setDescuentoLempiras] = useState("");
  const [selectedExamenes, setSelectedExamenes] = useState<ExamenTipoListItem[]>([]);
  const [selectedPerfiles, setSelectedPerfiles] = useState<PerfilExamenListItem[]>([]);

  // Track whether form has been initialized from the loaded order
  const [initialized, setInitialized] = useState(false);

  // Search state
  const [medicoSearch, setMedicoSearch] = useState("");
  const [clienteSearch, setClienteSearch] = useState("");
  const [examenSearch, setExamenSearch] = useState("");
  const [perfilSearch, setPerfilSearch] = useState("");
  const debouncedMedicoSearch = useDebounce(medicoSearch, 300);
  const debouncedClienteSearch = useDebounce(clienteSearch, 300);
  const debouncedExamenSearch = useDebounce(examenSearch, 300);
  const debouncedPerfilSearch = useDebounce(perfilSearch, 300);

  // Queries
  const { data: orden, isLoading: isLoadingOrden } = useOrden(ordenId, { enabled: open });
  const { data: listasPrecios } = useListasPrecios();
  const { data: medicosResults } = useBuscarMedicosRemitentes(debouncedMedicoSearch, {
    enabled: debouncedMedicoSearch.length >= 2 && !medicoId,
  });
  const { data: clientesResults } = useBuscarClientes(debouncedClienteSearch, {
    enabled: debouncedClienteSearch.length >= 2 && !clienteId,
  });
  const { data: examenesResults } = useBuscarExamenesTipo(debouncedExamenSearch, {
    enabled: debouncedExamenSearch.length >= 2,
  });
  const { data: perfilesResults } = useBuscarPerfiles(debouncedPerfilSearch, {
    enabled: debouncedPerfilSearch.length >= 2,
  });

  // Mutation
  const updateMutation = useUpdateOrden({
    onSuccess: () => {
      toast.success(t("ordenes.edit.success"));
      onOpenChange(false);
    },
    onError: (err) => {
      // Check for orden_no_editable — show specific reason if available
      const errObj = err as { response?: { data?: { error_code?: string } } };
      const errorCode = errObj?.response?.data?.error_code;
      if (errorCode === "orden_no_editable" && orden) {
        const motivoKey = getMotivoNoEditable(orden.tipo_cobro, orden.estado);
        if (motivoKey) {
          toast.error(t(motivoKey));
          return;
        }
      }
      toast.error(getErrorMessage(err));
    },
  });

  // Pre-load order values when dialog opens and order data is available
  useEffect(() => {
    if (!open) {
      setInitialized(false);
      return;
    }
    if (!orden || initialized) return;

    setMedicoId(orden.medico_remitente);
    setMedicoNombre(orden.medico_remitente_nombre ?? "");
    setClienteId(orden.cliente);
    setClienteNombre(orden.cliente_nombre ?? "");
    setListaPrecioId(orden.lista_precio);
    setPrioridad(orden.prioridad);
    setTipoEntrega(orden.tipo_entrega);
    setTipoServicio(orden.tipo_servicio);
    setObservaciones(orden.observaciones ?? "");
    setDescuentoPorcentaje(
      orden.descuento_porcentaje > 0 ? String(orden.descuento_porcentaje) : ""
    );
    setDescuentoLempiras(orden.descuento_lempiras > 0 ? String(orden.descuento_lempiras) : "");

    // Build selected exams from active orden examenes
    // We only have OrdenExamen items here — map them to ExamenTipoListItem shape
    const examenesSinPerfil = orden.examenes
      .filter((oe) => oe.is_active && !oe.perfil_origen)
      .map((oe) => ({
        id: oe.examen_tipo,
        codigo: oe.examen_tipo_codigo,
        nombre: oe.examen_tipo_nombre,
        nombre_corto: oe.examen_tipo_nombre,
        area: oe.examen_tipo_area as ExamenTipoListItem["area"],
        area_display: oe.examen_tipo_area,
        unidad: "",
        precio: oe.precio,
        requiere_ayuno: false,
        is_active: true,
      }));
    setSelectedExamenes(examenesSinPerfil);

    // Perfiles: collect unique perfil_origen ids and build minimal PerfilExamenListItem
    const perfilMap = new Map<number, PerfilExamenListItem>();
    for (const oe of orden.examenes.filter((e) => e.is_active && e.perfil_origen)) {
      if (oe.perfil_origen && !perfilMap.has(oe.perfil_origen)) {
        perfilMap.set(oe.perfil_origen, {
          id: oe.perfil_origen,
          codigo: "",
          nombre: oe.perfil_origen_nombre ?? "",
          precio: 0,
          precio_individual_total: 0,
          ahorro: 0,
          num_examenes: 0,
          examenes_nombres: [],
          examenes_ids: [],
          descripcion: "",
          is_active: true,
        });
      }
    }
    setSelectedPerfiles(Array.from(perfilMap.values()));

    // Clear search fields
    setMedicoSearch("");
    setClienteSearch("");
    setExamenSearch("");
    setPerfilSearch("");
    setInitialized(true);
  }, [open, orden, initialized]);

  // Derived values
  const listaPrecioActiva = useMemo(
    () => listasPrecios?.find((lp) => lp.id === listaPrecioId) ?? null,
    [listasPrecios, listaPrecioId]
  );
  const porcentajeAjuste = listaPrecioActiva?.porcentaje_ajuste ?? 0;

  const perfilExamenIds = useMemo(
    () => new Set(selectedPerfiles.flatMap((p) => p.examenes_ids ?? [])),
    [selectedPerfiles]
  );

  const filteredExamResults = examenesResults?.filter(
    (e) => !selectedExamenes.some((s) => s.id === e.id) && !perfilExamenIds.has(e.id)
  );
  const filteredPerfilResults = perfilesResults?.filter(
    (p) => !selectedPerfiles.some((s) => s.id === p.id)
  );

  // Exam/profile handlers
  const handleAddExamen = useCallback(
    (examen: ExamenTipoListItem) => {
      if (perfilExamenIds.has(examen.id)) {
        toast.warning(t("ordenes.duplicateExam.alreadyInProfile"));
        return;
      }
      setSelectedExamenes((prev) => {
        if (prev.some((e) => e.id === examen.id)) return prev;
        return [...prev, examen];
      });
      setExamenSearch("");
    },
    [perfilExamenIds, t]
  );

  const handleRemoveExamen = useCallback((id: number) => {
    setSelectedExamenes((prev) => prev.filter((e) => e.id !== id));
  }, []);

  const handleAddPerfil = useCallback(
    (perfil: PerfilExamenListItem) => {
      setSelectedPerfiles((prev) => {
        if (prev.some((p) => p.id === perfil.id)) return prev;
        return [...prev, perfil];
      });
      const perfilIds = new Set(perfil.examenes_ids ?? []);
      setSelectedExamenes((prev) => {
        const duplicados = prev.filter((e) => perfilIds.has(e.id));
        if (duplicados.length > 0) {
          toast.info(
            t("ordenes.duplicateExam.removedFromList", {
              count: duplicados.length,
              perfil: perfil.nombre,
            })
          );
          return prev.filter((e) => !perfilIds.has(e.id));
        }
        return prev;
      });
      setPerfilSearch("");
    },
    [t]
  );

  const handleRemovePerfil = useCallback((id: number) => {
    setSelectedPerfiles((prev) => prev.filter((p) => p.id !== id));
  }, []);

  // Totals
  const totalExamenesAjustado = selectedExamenes.reduce(
    (sum, e) => sum + calcularPrecioAjustado(Number(e.precio), porcentajeAjuste),
    0
  );
  const totalPerfilesAjustado = selectedPerfiles.reduce(
    (sum, p) => sum + calcularPrecioAjustado(Number(p.precio), porcentajeAjuste),
    0
  );
  const subtotalBase =
    selectedExamenes.reduce((sum, e) => sum + Number(e.precio), 0) +
    selectedPerfiles.reduce((sum, p) => sum + Number(p.precio), 0);
  const subtotal = totalExamenesAjustado + totalPerfilesAjustado;
  const ajusteListaPrecio = subtotal - subtotalBase;
  const pctVal = parseFloat(descuentoPorcentaje) || 0;
  const lpsVal = parseFloat(descuentoLempiras) || 0;
  const afterPct = subtotal * (1 - Math.min(Math.max(pctVal, 0), 100) / 100);
  const totalGeneral = Math.max(afterPct - Math.max(lpsVal, 0), 0);

  const handleSubmit = () => {
    if (selectedExamenes.length === 0 && selectedPerfiles.length === 0) {
      toast.error(t("ordenes.mustSelectExam"));
      return;
    }

    // Build patch payload with only modified fields
    const patch: Record<string, unknown> = {};

    if (orden) {
      if (medicoId !== orden.medico_remitente) patch.medico_remitente = medicoId;
      if (clienteId !== orden.cliente) patch.cliente = clienteId;
      if (listaPrecioId !== orden.lista_precio) patch.lista_precio = listaPrecioId;
      if (prioridad !== orden.prioridad) patch.prioridad = prioridad;
      if (tipoEntrega !== orden.tipo_entrega) patch.tipo_entrega = tipoEntrega;
      if (tipoServicio !== orden.tipo_servicio) patch.tipo_servicio = tipoServicio;
      if (observaciones !== (orden.observaciones ?? "")) patch.observaciones = observaciones;

      const newPct = parseFloat(descuentoPorcentaje) || 0;
      const newLps = parseFloat(descuentoLempiras) || 0;
      if (newPct !== Number(orden.descuento_porcentaje)) patch.descuento_porcentaje = newPct;
      if (newLps !== Number(orden.descuento_lempiras)) patch.descuento_lempiras = newLps;

      // Always send exams/perfiles so backend can sync
      patch.examenes_ids = selectedExamenes.map((e) => e.id);
      patch.perfiles_ids = selectedPerfiles.map((p) => p.id);
    }

    updateMutation.mutate({ id: ordenId, data: patch });
  };

  const hasItems = selectedExamenes.length > 0 || selectedPerfiles.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("ordenes.edit.title")}</DialogTitle>
          <DialogDescription>
            {orden
              ? `${t("ordenes.edit.description")} — ${orden.numero}`
              : t("ordenes.edit.description")}
          </DialogDescription>
        </DialogHeader>

        {isLoadingOrden || !initialized ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-4">
            {/* Médico remitente */}
            <div className="space-y-2">
              <Label>{t("ordenes.fields.doctor")}</Label>
              {medicoId ? (
                <div className="flex items-center gap-2 p-2 border rounded-md bg-muted/50">
                  <span className="flex-1 font-medium">{medicoNombre}</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={() => {
                      setMedicoId(null);
                      setMedicoNombre("");
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <div className="space-y-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      placeholder={t("ordenes.searchDoctor")}
                      value={medicoSearch}
                      onChange={(e) => setMedicoSearch(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                  {medicosResults && medicosResults.length > 0 && (
                    <div className="border rounded-md max-h-40 overflow-y-auto">
                      {medicosResults.map((m) => (
                        <button
                          key={m.id}
                          className="w-full text-left px-3 py-2 hover:bg-muted text-sm flex justify-between"
                          onClick={() => {
                            setMedicoId(m.id);
                            setMedicoNombre(m.nombre_completo);
                            setMedicoSearch("");
                          }}
                        >
                          <span className="font-medium">{m.nombre_completo}</span>
                          <span className="text-muted-foreground text-xs">{m.especialidad}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground">{t("ordenes.doctorHint")}</p>
                </div>
              )}
            </div>

            {/* Cliente */}
            <div className="space-y-2">
              <Label>{t("ordenes.fields.client")}</Label>
              {clienteId ? (
                <div className="flex items-center gap-2 p-2 border rounded-md bg-muted/50">
                  <span className="flex-1 font-medium">{clienteNombre}</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={() => {
                      setClienteId(null);
                      setClienteNombre("");
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <div className="space-y-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      placeholder={t("ordenes.searchClient")}
                      value={clienteSearch}
                      onChange={(e) => setClienteSearch(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                  {clientesResults && clientesResults.length > 0 && (
                    <div className="border rounded-md max-h-40 overflow-y-auto">
                      {clientesResults.map((c) => (
                        <button
                          key={c.id}
                          className="w-full text-left px-3 py-2 hover:bg-muted text-sm flex justify-between"
                          onClick={() => {
                            setClienteId(c.id);
                            setClienteNombre(c.nombre);
                            setClienteSearch("");
                            if (c.lista_precio) setListaPrecioId(c.lista_precio);
                          }}
                        >
                          <span className="font-medium">{c.nombre}</span>
                          <span className="text-muted-foreground font-mono text-xs">
                            {c.codigo}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground">{t("ordenes.clientHint")}</p>
                </div>
              )}
            </div>

            {/* Lista de Precios */}
            <div className="space-y-2">
              <Label>{t("ordenes.listaPrecio.label")}</Label>
              <Select
                value={listaPrecioId?.toString() ?? "none"}
                onValueChange={(v) => setListaPrecioId(v === "none" ? null : Number(v))}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t("ordenes.listaPrecio.placeholder")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t("ordenes.listaPrecio.standard")}</SelectItem>
                  {listasPrecios?.map((lp) => (
                    <SelectItem key={lp.id} value={lp.id.toString()}>
                      {formatListaPrecioLabel(lp.nombre, lp.porcentaje_ajuste)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Prioridad, Tipo de Entrega y Tipo de Servicio */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t("ordenes.fields.priority")}</Label>
                <Select value={prioridad} onValueChange={(v) => setPrioridad(v as PrioridadOrden)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="NORMAL">{t("ordenes.priority.normal")}</SelectItem>
                    <SelectItem value="URGENTE">{t("ordenes.priority.urgent")}</SelectItem>
                    <SelectItem value="STAT">{t("ordenes.priority.stat")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t("ordenes.fields.deliveryType")}</Label>
                <Select value={tipoEntrega} onValueChange={(v) => setTipoEntrega(v as TipoEntrega)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PRESENCIAL">{t("ordenes.delivery.presencial")}</SelectItem>
                    <SelectItem value="EMAIL">{t("ordenes.delivery.email")}</SelectItem>
                    <SelectItem value="WHATSAPP">{t("ordenes.delivery.whatsapp")}</SelectItem>
                    <SelectItem value="PORTAL">{t("ordenes.delivery.portal")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Tipo de Servicio */}
            <div className="space-y-2">
              <Label>{t("ordenes.fields.serviceType")}</Label>
              <Select
                value={tipoServicio}
                onValueChange={(v) => setTipoServicio(v as TipoServicio)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="AMBULATORIO">
                    {t("ordenes.serviceType.ambulatorio")}
                  </SelectItem>
                  <SelectItem value="EMERGENCIA">{t("ordenes.serviceType.emergencia")}</SelectItem>
                  <SelectItem value="HOSPITALIZACION">
                    {t("ordenes.serviceType.hospitalizacion")}
                  </SelectItem>
                  <SelectItem value="DOMICILIO">{t("ordenes.serviceType.domicilio")}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Separator />

            {/* Tabs Exámenes / Perfiles */}
            <Tabs defaultValue="examenes" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="examenes" className="flex items-center gap-2">
                  <TestTubes className="h-4 w-4" />
                  {t("ordenes.fields.exams")}
                </TabsTrigger>
                <TabsTrigger value="perfiles" className="flex items-center gap-2">
                  <Package className="h-4 w-4" />
                  {t("ordenes.fields.profiles")}
                </TabsTrigger>
              </TabsList>

              {/* Tab Exámenes */}
              <TabsContent value="examenes" className="space-y-3">
                <div className="space-y-2">
                  <Label>{t("ordenes.fields.exams")}</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      placeholder={t("ordenes.searchExams")}
                      value={examenSearch}
                      onChange={(e) => setExamenSearch(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                  {filteredExamResults && filteredExamResults.length > 0 && (
                    <div className="border rounded-md max-h-40 overflow-y-auto">
                      {filteredExamResults.map((e) => (
                        <button
                          key={e.id}
                          className="w-full text-left px-3 py-2 hover:bg-muted text-sm flex justify-between items-center"
                          onClick={() => handleAddExamen(e)}
                        >
                          <div>
                            <span className="font-mono text-xs text-muted-foreground mr-2">
                              {e.codigo}
                            </span>
                            <span>{e.nombre}</span>
                          </div>
                          <span className="text-muted-foreground">
                            L {Number(e.precio).toFixed(2)}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {selectedExamenes.length > 0 ? (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>
                        {t("ordenes.selectedExams")} ({selectedExamenes.length})
                      </Label>
                      <span className="text-sm font-medium">
                        L {totalExamenesAjustado.toFixed(2)}
                      </span>
                    </div>
                    <div className="max-h-48 overflow-y-auto space-y-1">
                      {selectedExamenes.map((e) => (
                        <div
                          key={e.id}
                          className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 sm:gap-2 p-2 border rounded-md text-sm"
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <TestTubes className="h-4 w-4 shrink-0 text-muted-foreground" />
                            <span className="font-mono text-xs text-muted-foreground">
                              {e.codigo}
                            </span>
                            <span className="truncate">{e.nombre}</span>
                            {e.area_display && (
                              <Badge variant="outline" className="text-xs shrink-0">
                                {e.area_display}
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-2 pl-6 sm:pl-0 shrink-0">
                            {porcentajeAjuste !== 0 ? (
                              <span className="flex items-center gap-1">
                                <span className="text-muted-foreground line-through text-xs">
                                  L {Number(e.precio).toFixed(2)}
                                </span>
                                <span className="font-medium">
                                  L{" "}
                                  {calcularPrecioAjustado(
                                    Number(e.precio),
                                    porcentajeAjuste
                                  ).toFixed(2)}
                                </span>
                              </span>
                            ) : (
                              <span className="text-muted-foreground">
                                L {Number(e.precio).toFixed(2)}
                              </span>
                            )}
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={() => handleRemoveExamen(e.id)}
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    {t("ordenes.noExamsSelected")}
                  </p>
                )}
              </TabsContent>

              {/* Tab Perfiles */}
              <TabsContent value="perfiles" className="space-y-3">
                <div className="space-y-2">
                  <Label>{t("ordenes.fields.profiles")}</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      placeholder={t("ordenes.searchProfiles")}
                      value={perfilSearch}
                      onChange={(e) => setPerfilSearch(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                  {filteredPerfilResults && filteredPerfilResults.length > 0 && (
                    <div className="border rounded-md max-h-40 overflow-y-auto">
                      {filteredPerfilResults.map((p) => (
                        <button
                          key={p.id}
                          className="w-full text-left px-3 py-2 hover:bg-muted text-sm space-y-1"
                          onClick={() => handleAddPerfil(p)}
                        >
                          <div className="flex justify-between items-start">
                            <div>
                              <span className="font-mono text-xs text-muted-foreground mr-2">
                                {p.codigo}
                              </span>
                              <span className="font-medium">{p.nombre}</span>
                            </div>
                            <div className="text-right">
                              <div className="text-muted-foreground">
                                L {Number(p.precio).toFixed(2)}
                              </div>
                              {p.ahorro > 0 && (
                                <div className="text-xs text-green-600">
                                  {t("ordenes.savings")}: L {Number(p.ahorro).toFixed(2)}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {t("ordenes.includesExams", { count: p.num_examenes })}:{" "}
                            {p.examenes_nombres.join(", ")}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {selectedPerfiles.length > 0 ? (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>
                        {t("ordenes.selectedProfiles")} ({selectedPerfiles.length})
                      </Label>
                      <span className="text-sm font-medium">
                        L {totalPerfilesAjustado.toFixed(2)}
                      </span>
                    </div>
                    <div className="max-h-56 overflow-y-auto space-y-1">
                      {selectedPerfiles.map((p) => (
                        <div key={p.id} className="p-3 border rounded-md text-sm space-y-2">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Package className="h-4 w-4 shrink-0 text-muted-foreground" />
                              {p.codigo && (
                                <span className="font-mono text-xs text-muted-foreground">
                                  {p.codigo}
                                </span>
                              )}
                              <span className="font-medium">{p.nombre}</span>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              {p.precio > 0 &&
                                (porcentajeAjuste !== 0 ? (
                                  <span className="flex items-center gap-1">
                                    <span className="text-muted-foreground line-through text-xs">
                                      L {Number(p.precio).toFixed(2)}
                                    </span>
                                    <span className="font-medium">
                                      L{" "}
                                      {calcularPrecioAjustado(
                                        Number(p.precio),
                                        porcentajeAjuste
                                      ).toFixed(2)}
                                    </span>
                                  </span>
                                ) : (
                                  <span className="text-muted-foreground">
                                    L {Number(p.precio).toFixed(2)}
                                  </span>
                                ))}
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6"
                                onClick={() => handleRemovePerfil(p.id)}
                              >
                                <X className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                          {p.examenes_nombres.length > 0 && (
                            <div className="pl-6 text-xs text-muted-foreground">
                              {t("ordenes.includesExams", { count: p.num_examenes })}:{" "}
                              {p.examenes_nombres.join(", ")}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    {t("ordenes.noProfilesSelected")}
                  </p>
                )}
              </TabsContent>
            </Tabs>

            {/* Descuentos */}
            {hasItems && (
              <div className="space-y-3">
                <Label className="flex items-center gap-2">
                  <Percent className="h-4 w-4" />
                  {t("ordenes.discount.title")}
                </Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">
                      {t("ordenes.discount.percentage")}
                    </Label>
                    <div className="relative">
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        placeholder="0"
                        value={descuentoPorcentaje}
                        onChange={(e) => setDescuentoPorcentaje(e.target.value)}
                        className="pr-8"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
                        %
                      </span>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">
                      {t("ordenes.discount.lempiras")}
                    </Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
                        L.
                      </span>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="0.00"
                        value={descuentoLempiras}
                        onChange={(e) => setDescuentoLempiras(e.target.value)}
                        className="pl-9"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Total */}
            {hasItems && (
              <div className="p-3 bg-muted/50 rounded-md space-y-2">
                {listaPrecioActiva && (
                  <div className="flex items-center gap-2 pb-1">
                    <Badge variant="secondary" className="text-xs">
                      {formatListaPrecioLabel(
                        listaPrecioActiva.nombre,
                        listaPrecioActiva.porcentaje_ajuste
                      )}
                    </Badge>
                  </div>
                )}
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Subtotal base</span>
                  <span>L {subtotalBase.toFixed(2)}</span>
                </div>
                {listaPrecioActiva && porcentajeAjuste !== 0 && (
                  <div
                    className={`flex items-center justify-between text-sm ${ajusteListaPrecio >= 0 ? "text-orange-600" : "text-green-600"}`}
                  >
                    <span>
                      {ajusteListaPrecio >= 0 ? "↑" : "↓"} {listaPrecioActiva.nombre} (
                      {porcentajeAjuste >= 0 ? "+" : ""}
                      {porcentajeAjuste}%)
                    </span>
                    <span>
                      {ajusteListaPrecio >= 0 ? "+ " : "- "}L{" "}
                      {Math.abs(ajusteListaPrecio).toFixed(2)}
                    </span>
                  </div>
                )}
                {listaPrecioActiva && porcentajeAjuste !== 0 && (
                  <div className="flex items-center justify-between text-sm font-medium border-t pt-1">
                    <span className="text-muted-foreground">Subtotal ajustado</span>
                    <span>L {subtotal.toFixed(2)}</span>
                  </div>
                )}
                {(pctVal > 0 || lpsVal > 0) && (
                  <>
                    {pctVal > 0 && (
                      <div className="flex items-center justify-between text-sm text-green-600">
                        <span>
                          {t("ordenes.discount.percentage")} ({pctVal}%)
                        </span>
                        <span>- L {((subtotal * pctVal) / 100).toFixed(2)}</span>
                      </div>
                    )}
                    {lpsVal > 0 && (
                      <div className="flex items-center justify-between text-sm text-green-600">
                        <span>{t("ordenes.discount.lempiras")}</span>
                        <span>- L {Math.min(lpsVal, afterPct).toFixed(2)}</span>
                      </div>
                    )}
                  </>
                )}
                <Separator />
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Calculator className="h-4 w-4" />
                    <span className="font-medium">Total</span>
                  </div>
                  <span className="text-lg font-bold">L {totalGeneral.toFixed(2)}</span>
                </div>
              </div>
            )}

            {/* Observaciones */}
            <div className="space-y-2">
              <Label>{t("ordenes.fields.observations")}</Label>
              <Textarea
                placeholder={t("ordenes.fields.observationsPlaceholder")}
                value={observaciones}
                onChange={(e) => setObservaciones(e.target.value)}
                rows={2}
              />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={updateMutation.isPending}
          >
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!hasItems || isLoadingOrden || !initialized || updateMutation.isPending}
          >
            {updateMutation.isPending ? t("common.saving") : t("common.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
